import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useAccounts } from "@/hooks/use-accounts";
import { useKit } from "@/hooks/use-kit";
import { useSchedulingConfig } from "@/hooks/use-scheduling-config";
import { useAutomationConfig } from "@/hooks/use-automation-config";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X } from "lucide-react";

const DAY_MS = 24 * 60 * 60 * 1000;

function dismissKey(brandId: string): string {
  return `setup-checklist-dismissed:${brandId}`;
}

/**
 * Dismissal rule (defines the spec's "reappears for brands < 7 days old"):
 * for a brand younger than 7 days a dismissal only suppresses the card for
 * 24h; once the brand is 7+ days old a dismissal is permanent.
 */
function isDismissed(brandId: string, brandCreatedAt: string): boolean {
  // Guarded like activity-bell: private mode / blocked storage degrades to
  // "checklist always shows", never a crash.
  let raw: string | null;
  try {
    raw = localStorage.getItem(dismissKey(brandId));
  } catch {
    return false;
  }
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;
  const brandAgeMs = Date.now() - new Date(brandCreatedAt).getTime();
  if (brandAgeMs < 7 * DAY_MS) {
    return Date.now() - dismissedAt < DAY_MS;
  }
  return true;
}

interface ChecklistItem {
  label: string;
  done: boolean;
  to: string;
}

/**
 * Brand setup checklist on the Overview page. Computed entirely from
 * existing hooks — no new endpoints. Hidden while loading (no flash),
 * at 4/4, or when dismissed per the rule above.
 */
export function SetupChecklist() {
  const { t } = useTranslation("dashboard");
  const { brand } = useBrandContext();
  const accounts = useAccounts(brand.id);
  const kit = useKit(brand.id);
  const scheduling = useSchedulingConfig(brand.id);
  const automation = useAutomationConfig(brand.id);

  // Re-render trigger after dismiss (localStorage isn't reactive).
  const [, setDismissedTick] = useState(0);

  if (
    accounts.isLoading || kit.isLoading || scheduling.isLoading || automation.isLoading ||
    accounts.isError || kit.isError || scheduling.isError || automation.isError
  ) {
    return null;
  }

  if (isDismissed(brand.id, brand.createdAt)) return null;

  const auto = automation.data;
  const items: ChecklistItem[] = [
    {
      label: t("setupChecklist.connectAccount"),
      done: (accounts.data ?? []).some((a) => a.status === "ACTIVE"),
      to: `/${brand.slug}/settings/accounts`,
    },
    {
      label: t("setupChecklist.setBrandVoice"),
      done: !!kit.data?.voiceProfile?.trim(),
      to: `/${brand.slug}/settings/kit`,
    },
    {
      label: t("setupChecklist.configureWindows"),
      done: (scheduling.data?.defaultWindows?.length ?? 0) > 0,
      to: `/${brand.slug}/settings/scheduling`,
    },
    {
      label: t("setupChecklist.turnOnAutomation"),
      done: !!(
        auto &&
        (auto.aiReviewEnabled ||
          auto.autoScheduleEnabled ||
          auto.autopilotEnabled ||
          auto.inboxAiDraftsEnabled ||
          auto.adsEnabled)
      ),
      to: `/${brand.slug}/settings/automation`,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  if (doneCount === items.length) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(brand.id), String(Date.now()));
    } catch {
      // private mode — the card simply reappears next session
    }
    setDismissedTick((prev) => prev + 1);
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{t("setupChecklist.title", { name: brand.name })}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("setupChecklist.progress", { done: doneCount, total: items.length })}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={t("setupChecklist.dismissLabel")}
          onClick={handleDismiss}
        >
          <X size={14} />
        </Button>
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${(doneCount / items.length) * 100}%` }}
        />
      </div>

      <ul className="mt-3 space-y-1.5">
        {items.map((item) =>
          item.done ? (
            <li key={item.label} className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 size={15} className="flex-shrink-0 text-green-500" />
              <span className="line-through">{item.label}</span>
            </li>
          ) : (
            <li key={item.label}>
              <Link
                to={item.to}
                className="flex items-center gap-2 text-sm hover:text-foreground hover:underline"
              >
                <Circle size={15} className="flex-shrink-0 text-muted-foreground/50" />
                {item.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
